import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { LotusMark } from "./LotusMark";

const lineEase = [0.76, 0, 0.24, 1];

const Line = ({ children, delay }) => (
  <span className="gt-line-mask">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.5, delay, ease: lineEase }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen overflow-hidden">
      {/* brand logo mark, right side */}
      <motion.div
        style={{ y: imgY }}
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.6, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="absolute right-0 md:right-[4%] top-0 h-full w-full md:w-[42%] flex items-center justify-center pointer-events-none"
      >
        <img
          src="/logo-mark.png"
          alt="The Golden Touch by Marisol logo"
          className="w-[62%] max-w-[420px] opacity-[0.16] md:opacity-90 drop-shadow-[0_0_60px_rgba(198,161,91,0.25)]"
        />
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12 min-h-screen flex flex-col justify-center pt-24"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="flex items-center gap-4 mb-10"
        >
          <LotusMark size={40} />
          <span className="gt-eyebrow">Est. Therapeutic Massage · 10+ Years</span>
        </motion.div>

        <h1 className="font-serif font-light leading-[0.92] tracking-tight text-[var(--gt-ivory)] text-6xl sm:text-7xl md:text-8xl lg:text-[9rem]">
          <Line delay={0.5}>The</Line>
          <Line delay={0.65}>
            <span className="gt-gold-text italic">Golden</span> Touch
          </Line>
          <Line delay={0.8}>
            <span className="text-3xl sm:text-4xl md:text-5xl italic text-[var(--gt-muted)]">
              by Marisol
            </span>
          </Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="mt-10 max-w-md text-[15px] leading-relaxed text-[var(--gt-muted)]"
        >
          Custom massage techniques to release stress from the body, mind and soul —
          dedicated to all body types and sizes.
        </motion.p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.4, duration: 1 }}
        className="absolute bottom-8 left-6 md:left-12 z-10 gt-eyebrow flex items-center gap-3"
      >
        <span className="h-8 w-px bg-[var(--gt-gold)] animate-pulse" />
        Scroll to begin
      </motion.div>
    </section>
  );
};
